// ConfigurarSemaforo.jsx
import React, { useEffect, useState } from "react";
import {
  postConfiguracionSemaforo,
  getTodasConfiguracionesSemaforo,
} from "../../componentes/http/actions/ClientesAction";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Typography,
  Button,
  IconButton,
  Box,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AddIcon from "@mui/icons-material/Add";
import FilterComponent from "../../componentes/data/FilterComponent";
import CrearSemaforoModal from "../../componentes/modal/Clientes/CrearSemaforoModal";
import EditarSemaforoModal from "../../componentes/modal/Clientes/EditarSemaforoModal";

const ConfigurarSemaforo = () => {
  const [configuraciones, setConfiguraciones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [openCrearModal, setOpenCrearModal] = useState(false);
  const [openEditarModal, setOpenEditarModal] = useState(false);
  const [configuracionSeleccionada, setConfiguracionSeleccionada] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterHabilitado, setFilterHabilitado] = useState("todos");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchConfiguraciones();
  }, []);

  const fetchConfiguraciones = async () => {
    setIsLoading(true);
    try {
      const response = await getTodasConfiguracionesSemaforo(localStorage.getItem("token"));
      setConfiguraciones(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error al cargar configuraciones:", error);
      setSnackbar({
        open: true,
        message: "Error al cargar configuraciones",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setInitialDataLoaded(true);
    }
  };

  const filteredConfiguraciones = configuraciones
    .filter((config) => {
      const matchesSearch = config.nombreCliente
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
        config.nombreModulo
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterHabilitado === "todos" ||
        (filterHabilitado === "habilitado" && config.habilitado === true) ||
        (filterHabilitado === "deshabilitado" && config.habilitado === false);
      return matchesSearch && matchesFilter;
    })
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPage(0);
  };
  
  const handleFilterChange = (newFilter) => {
    setFilterHabilitado(newFilter);
    setPage(0);
  };

  const filterOptions = [
    { value: "todos", label: "Todos" },
    { value: "habilitado", label: "Habilitados" },
    { value: "deshabilitado", label: "Deshabilitados" },
  ];

  const handleEditarConfiguracion = (configuracion) => {
    setConfiguracionSeleccionada(configuracion);
    setOpenEditarModal(true);
  };

  const handleCrearConfiguracion = () => {
    setOpenCrearModal(true);
  };

  const handleConfigurarSemaforo = async (configuracionActualizada) => {
    try {
      setIsLoading(true);
      
      await postConfiguracionSemaforo(localStorage.getItem("token"), configuracionActualizada);
      
      setSnackbar({
        open: true,
        message: configuracionSeleccionada 
          ? "Configuración de semáforo actualizada con éxito"
          : "Configuración de semáforo creada con éxito",
        severity: "success",
      });
      
      await fetchConfiguraciones();
      
      // Cerrar ambos modales
      setOpenCrearModal(false);
      setOpenEditarModal(false);
      setConfiguracionSeleccionada(null);
    } catch (error) {
      console.error("Error al procesar configuración:", error);
      
      let errorMessage = "Error al procesar la configuración del semáforo";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message?.includes("cliente deshabilitado")) {
        errorMessage = "No se puede configurar el semáforo para un cliente deshabilitado";
      } else if (error.message?.includes("Ya existe")) {
        errorMessage = "Ya existe una configuración de semáforo para este cliente y módulo";
      } else if (error.message?.includes("no tiene acceso")) {
        errorMessage = "El cliente no tiene acceso al módulo de semáforo especificado";
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTipoAplicacionChip = (tipoAplicacion) => {
    const colorMap = {
      'Compromisos': 'primary',
      'Obligaciones': 'secondary',
      'Ambos': 'success'
    };
    
    return (
      <Chip 
        label={tipoAplicacion}
        color={colorMap[tipoAplicacion] || 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Grid container direction="column" spacing={2} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
      <Grid item>
        <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h4">Gestión de Configuraciones de Semáforos</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCrearConfiguracion}
              startIcon={<AddIcon />}
            >
              NUEVA CONFIGURACIÓN
            </Button>
        </Grid>
      </Grid>
      
      <Grid item>
        <FilterComponent
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          filterValue={filterHabilitado}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
          placeholder="Buscar por cliente o módulo..."
        />
      </Grid>
      
      <Grid item>
        <TableContainer component={Paper}>
          {isLoading && !initialDataLoaded ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : filteredConfiguraciones.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Aplicar a</TableCell>
                  <TableCell>Colores Configurados</TableCell>
                  <TableCell>Umbrales (B/M/A)</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell sx={{ display: "flex", justifyContent: "center" }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConfiguraciones.map((config, index) => (
                  <TableRow key={`${config.idCliente}-${index}`}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {config.nombreCliente}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {config.idCliente}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getTipoAplicacionChip(config.tipoAplicacion)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            backgroundColor: config.colorBaja,
                            border: '1px solid #ccc',
                            borderRadius: 1
                          }} 
                          title="Criticidad Baja"
                        />
                        <Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            backgroundColor: config.colorMedia,
                            border: '1px solid #ccc',
                            borderRadius: 1
                          }} 
                          title="Criticidad Media"
                        />
                        <Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            backgroundColor: config.colorAlta,
                            border: '1px solid #ccc',
                            borderRadius: 1
                          }} 
                          title="Criticidad Alta"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {config.cantidadBaja}/{config.cantidadMedia}/{config.cantidadAlta}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={config.habilitado ? "Si" : "No"}
                        color={config.habilitado ? "success" : "error"}
                        variant="contained"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                       <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                        <Tooltip title="Editar configuración de semáforo">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEditarConfiguracion(config)}
                          >
                            <EditNoteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box p={3}>
              <Typography>
                No se encontraron configuraciones de semáforos con los criterios de búsqueda actuales.
              </Typography>
            </Box>
          )}
        </TableContainer>
        
        <TablePagination
          component="div"
          count={configuraciones.filter((config) => {
            const matchesSearch = config.nombreCliente
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
              config.nombreModulo
              .toLowerCase()
              .includes(searchTerm.toLowerCase());
            const matchesFilter =
              filterHabilitado === "todos" ||
              (filterHabilitado === "habilitado" && config.habilitado === true) ||
              (filterHabilitado === "deshabilitado" && config.habilitado === false);
            return matchesSearch && matchesFilter;
          }).length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Grid>

      {/* Modal para crear nueva configuración */}
      <CrearSemaforoModal
        open={openCrearModal}
        onClose={() => setOpenCrearModal(false)}
        onSubmit={handleConfigurarSemaforo}
        configuracionesExistentes={configuraciones}
      />

      {/* Modal para editar configuración */}
      <EditarSemaforoModal
        open={openEditarModal}
        onClose={() => {
          setOpenEditarModal(false);
          setConfiguracionSeleccionada(null);
        }}
        onSubmit={handleConfigurarSemaforo}
        configuracionExistente={configuracionSeleccionada}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ConfigurarSemaforo;